/**
 * Utilitários para serviços com cobrança recorrente.
 *
 * Mantém num só lugar:
 *  - Opções de periodicidade, tipo de prazo e unidade de prazo
 *  - Labels amigáveis em PT-BR
 *  - Cálculo do total estimado do contrato (mensalidade × ciclos)
 *
 * Os campos correspondem às colunas adicionadas em `crm_products`:
 *  - recurring_price       numeric
 *  - recurrence_interval   enum crm_service_recurrence
 *  - term_type             enum crm_service_term_type
 *  - term_quantity         integer
 *  - term_unit             enum crm_service_term_unit
 */

export type RecurrenceInterval =
  | 'semanal'
  | 'quinzenal'
  | 'mensal'
  | 'bimestral'
  | 'trimestral'
  | 'semestral'
  | 'anual'
  | 'bienal';

export type TermUnit = 'dias' | 'meses' | 'anos';
export type TermType = 'determinado' | 'indeterminado';

// Opções para o <Select> de periodicidade
export const RECURRENCE_OPTIONS: { value: RecurrenceInterval; label: string }[] = [
  { value: 'semanal', label: 'Semanal' },
  { value: 'quinzenal', label: 'Quinzenal' },
  { value: 'mensal', label: 'Mensal' },
  { value: 'bimestral', label: 'Bimestral' },
  { value: 'trimestral', label: 'Trimestral' },
  { value: 'semestral', label: 'Semestral' },
  { value: 'anual', label: 'Anual' },
  { value: 'bienal', label: 'Bienal' },
];

// Opções para o tipo de prazo
export const TERM_TYPE_OPTIONS: { value: TermType; label: string }[] = [
  { value: 'determinado', label: 'Prazo determinado' },
  { value: 'indeterminado', label: 'Prazo indeterminado' },
];

// Opções para unidade do prazo (quando determinado)
export const TERM_UNIT_OPTIONS: { value: TermUnit; label: string }[] = [
  { value: 'dias', label: 'Dia(s)' },
  { value: 'meses', label: 'Mês(es)' },
  { value: 'anos', label: 'Ano(s)' },
];

// Conversão aproximada de cada intervalo para dias
const DAYS_PER_INTERVAL: Record<RecurrenceInterval, number> = {
  semanal: 7,
  quinzenal: 15,
  mensal: 30,
  bimestral: 60,
  trimestral: 90,
  semestral: 180,
  anual: 365,
  bienal: 730,
};

// Conversão aproximada da unidade de prazo para dias
const DAYS_PER_TERM_UNIT: Record<TermUnit, number> = {
  dias: 1,
  meses: 30,
  anos: 365,
};

/** Retorna label amigável para a periodicidade */
export function formatRecurrenceLabel(value?: RecurrenceInterval | null): string {
  if (!value) return '';
  return RECURRENCE_OPTIONS.find((o) => o.value === value)?.label || value;
}

/** Retorna label amigável para a unidade de prazo */
export function formatTermUnitLabel(value?: TermUnit | null): string {
  if (!value) return '';
  return TERM_UNIT_OPTIONS.find((o) => o.value === value)?.label || value;
}

/**
 * Formata o prazo completo: "12 Mês(es)" ou "Prazo indeterminado".
 */
export function formatTermLabel(
  termType?: TermType | null,
  qty?: number | null,
  unit?: TermUnit | null,
): string {
  if (termType === 'indeterminado') return 'Prazo indeterminado';
  if (!termType || !qty || !unit) return '';
  return `${qty} ${formatTermUnitLabel(unit)}`;
}

export interface RecurringTotalInput {
  recurring_price?: number | null;
  recurrence_interval?: RecurrenceInterval | null;
  term_type?: TermType | null;
  term_quantity?: number | null;
  term_unit?: TermUnit | null;
}

/**
 * Calcula o total estimado do contrato.
 * Retorna null quando o prazo é indeterminado ou os dados são insuficientes.
 *
 *   ciclos = floor(prazo_em_dias / dias_por_ciclo)
 *   total  = mensalidade * ciclos
 */
export function calculateRecurringTotal(input: RecurringTotalInput): number | null {
  const { recurring_price, recurrence_interval, term_type, term_quantity, term_unit } = input;

  if (!recurring_price || recurring_price <= 0) return null;
  if (!recurrence_interval) return null;
  if (term_type !== 'determinado') return null;
  if (!term_quantity || term_quantity <= 0 || !term_unit) return null;

  const daysPerCycle = DAYS_PER_INTERVAL[recurrence_interval];
  const termDays = term_quantity * DAYS_PER_TERM_UNIT[term_unit];

  if (!daysPerCycle || !termDays) return null;

  const cycles = Math.floor(termDays / daysPerCycle);
  if (cycles <= 0) return null;

  return recurring_price * cycles;
}

/** Verifica se um produto é serviço recorrente (helper de leitura) */
export function isRecurringService(
  productType?: string | null,
  unitType?: string | null,
): boolean {
  return productType === 'service' && unitType === 'recorrente';
}
